import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import { addWatermark } from '../utils/watermark';
import watermarkLogo from '../winston-smiley-white.svg';
import stripExif from '../utils/stripExif';
import AdminNav, { CREATOR_SIDEBAR_WIDTH, CREATOR_BREAKPOINT } from '../components/AdminNav';
import { theme, font, pillButton, outlineButton, inputStyle, labelStyle } from '../components/adminTheme';
import { useLang } from '../useLang';
import { uploadToB2 } from '../utils/uploadToB2';

function Upload() {
    const navigate = useNavigate();
    const { t } = useLang();
    const [albumTitle, setAlbumTitle] = useState('');
    const [region, setRegion] = useState('canada');
    const [files, setFiles] = useState([]);
    const [coverFile, setCoverFile] = useState(null);
    const [location, setLocation] = useState('');
    const [creator, setCreator] = useState('');
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [users, setUsers] = useState([]);
    const [description, setDescription] = useState('');
    const [heroFile, setHeroFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [confirmStep, setConfirmStep] = useState(0); // 0 = fermé, 1 = première confirmation, 2 = seconde
    const [sessionUserId, setSessionUserId] = useState(null);

    useEffect(() => {
        const checkUser = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) { navigate('/login'); return; }
            setSessionUserId(data.session.user.id);

            const { data: userData } = await supabase
                .from('users')
                .select('username, role, first_name, last_name, avatar_url')
                .eq('email', data.session?.user?.email)
                .single();

            if (userData) {
                setUsername(userData.username);
                setRole(userData.role);
                setAvatarUrl(userData.avatar_url || '');
                const myName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username;
                setCreator(myName);
            }

            if (userData?.role?.toLowerCase() === 'admin') {
                const { data: usersData } = await supabase
                    .from('users')
                    .select('username, first_name, last_name')
                    .order('username');
                if (usersData) setUsers(usersData);
            }
        };
        checkUser();
    }, [navigate]);

    const handlePublish = async () => {
        if (!albumTitle) { setUploadError(t('titleRequired')); setConfirmStep(0); return; }
        if (files.length === 0) { setUploadError(t('photoRequired')); setConfirmStep(0); return; }
        if (!creator) { setUploadError(t('creatorRequired')); setConfirmStep(0); return; }

        setUploading(true);
        setUploadError('');
        setConfirmStep(0);

        const folderName = albumTitle.toLowerCase().replace(/\s+/g, '-');

        let coverUrl = '';
        try {
            if (coverFile) {
                const cleanCover = await stripExif(coverFile);
                coverUrl = await uploadToB2(`covers/${folderName}/${cleanCover.name}`, cleanCover);
            }

            let heroUrl = '';
            if (heroFile) {
                heroUrl = await uploadToB2(`covers/${folderName}/hero_${heroFile.name}`, heroFile);
            }

            for (const file of files) {
                const watermarkedFile = await addWatermark(file, watermarkLogo);
                await uploadToB2(`photos/${region}/${folderName}/${file.name}`, watermarkedFile);
            }

            const { error } = await supabase.from('albums').insert({
                title: albumTitle,
                region,
                folder: folderName,
                date: new Date().toISOString().split('T')[0],
                location,
                cover_url: coverUrl,
                created_by: creator,
                created_by_user_id: sessionUserId,
                description,
                hero_url: heroUrl,
                status: 'published',
                archived: false,
            });

            setUploading(false);
            if (error) { setUploadError(t('somethingWrong')); return; }
            navigate('/admin/dashboard');
        } catch (err) {
            console.error('Erreur upload:', err);
            setUploading(false);
            setUploadError(t('somethingWrong'));
        }
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/');
    };

    return (
        <div style={{ minHeight: '100vh', background: theme.bg }}>
            <style>{`
                .cupload-content {
                    margin-left: ${CREATOR_SIDEBAR_WIDTH}px;
                    padding: 48px 40px 80px;
                    box-sizing: border-box;
                }
                @media (max-width: ${CREATOR_BREAKPOINT}px) {
                    .cupload-content { margin-left: 0; padding: 88px 24px 60px; }
                }
                .cupload-card {
                    background: ${theme.card};
                    border: 1px solid ${theme.border};
                    box-shadow: 0 1px 3px rgba(14,16,19,0.04);
                    padding: 24px;
                    max-width: 760px;
                    box-sizing: border-box;
                }
                .cupload-dropzone {
                    border: 1.5px dashed ${theme.border};
                    text-align: center;
                    cursor: pointer;
                    transition: border-color .2s ease;
                }
                .cupload-dropzone:hover { border-color: ${theme.borderHover}; }
                @keyframes cupload-spin { to { transform: rotate(360deg); } }
            `}</style>

            <AdminNav username={username} role={role} avatarUrl={avatarUrl} onLogout={handleLogout} activePath="/admin/upload" />

            {uploading && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.92)', zIndex: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                    <div style={{ width: '32px', height: '32px', border: `2px solid ${theme.border}`, borderTop: `2px solid ${theme.text}`, borderRadius: '50%', animation: 'cupload-spin 0.8s linear infinite' }} />
                    <p style={{ fontFamily: font, fontSize: '13px', color: theme.textMuted }}>{t('uploading')}</p>
                </div>
            )}

            <div className="cupload-content">
                <p style={{ ...labelStyle, marginBottom: '6px' }}>{t('newAlbumTitle')}</p>
                <h1 style={{ fontFamily: font, fontWeight: 700, fontSize: 'clamp(20px, 2.4vw, 26px)', letterSpacing: '-0.01em', color: theme.text, margin: '0 0 6px' }}>
                    {t('uploadPhotos')}
                </h1>
                <p style={{ fontFamily: font, fontSize: '13px', fontWeight: 500, color: theme.textMuted, margin: '0 0 28px' }}>{t('uploadSubtitle')}</p>

                {uploadError && (
                    <p style={{ fontFamily: font, color: theme.danger, fontSize: '13px', fontWeight: 500, marginBottom: '16px', background: '#fdf2f1', border: '1px solid #f3d9d6', padding: '10px 14px' }}>⚠ {uploadError}</p>
                )}

                <div className="cupload-card">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                        <div>
                            <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('albumTitle')}</p>
                            <input type="text" value={albumTitle} onChange={e => setAlbumTitle(e.target.value)} placeholder="Ex: Ottawa — Archive 1" style={{ ...inputStyle, fontSize: '13.5px' }} />
                        </div>
                        <div>
                            <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('region')}</p>
                            <select value={region} onChange={e => setRegion(e.target.value)} style={{ ...inputStyle, fontSize: '13.5px' }}>
                                <option value="canada">Canada</option>
                                <option value="asia">Asia</option>
                                <option value="europe">Europe</option>
                            </select>
                        </div>
                        <div>
                            <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('location')}</p>
                            <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="Ex: Ottawa, Ontario, Canada" style={{ ...inputStyle, fontSize: '13.5px' }} />
                        </div>
                        <div>
                            <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('creator')}</p>
                            {role?.toLowerCase() === 'admin' ? (
                                <select value={creator} onChange={e => setCreator(e.target.value)} style={{ ...inputStyle, fontSize: '13.5px' }}>
                                    <option value="">{t('selectCreator')}</option>
                                    {users.map(u => {
                                        const label = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
                                        return <option key={u.username} value={label}>{label} (@{u.username})</option>;
                                    })}
                                </select>
                            ) : (
                                <p style={{ fontFamily: font, fontSize: '13.5px', fontWeight: 500, color: theme.text, padding: '10px 0', margin: 0 }}>
                                    {creator} <span style={{ color: theme.textMuted, fontSize: '11px' }}>@{username}</span>
                                </p>
                            )}
                        </div>
                    </div>

                    <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('albumCover')}</p>
                    <div className="cupload-dropzone" style={{ padding: '20px', marginBottom: '20px' }} onClick={() => document.getElementById('coverInput').click()}>
                        <p style={{ fontFamily: font, color: theme.textMuted, fontSize: '13.5px', fontWeight: 500, margin: 0 }}>{coverFile ? coverFile.name : t('clickSelectCover')}</p>
                        <input id="coverInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setCoverFile(e.target.files[0])} />
                    </div>

                    <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('description')}</p>
                    <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder={t('descriptionPlaceholder')} style={{ ...inputStyle, resize: 'vertical', marginBottom: '20px' }} />

                    <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('hero')}</p>
                    <div className="cupload-dropzone" style={{ padding: '20px', marginBottom: heroFile ? '10px' : '20px' }} onClick={() => document.getElementById('heroInput').click()}>
                        <p style={{ fontFamily: font, color: theme.textMuted, fontSize: '13.5px', fontWeight: 500, margin: 0 }}>{heroFile ? heroFile.name : t('clickSelectHero')}</p>
                        <input id="heroInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setHeroFile(e.target.files[0])} />
                    </div>
                    {heroFile && (
                        <img src={URL.createObjectURL(heroFile)} alt="hero preview" style={{ width: '100%', height: '120px', objectFit: 'cover', marginBottom: '20px' }} />
                    )}

                    <p style={{ ...labelStyle, margin: '0 0 6px' }}>{t('photos')}</p>
                    <div className="cupload-dropzone" style={{ padding: '36px', marginBottom: '24px' }} onClick={() => document.getElementById('fileInput').click()}>
                        <p style={{ fontSize: '22px', margin: 0 }}>☁️</p>
                        <p style={{ fontFamily: font, color: theme.textMuted, fontSize: '13.5px', fontWeight: 500 }}>{files.length > 0 ? `${files.length} ${t('photosSelected')}` : t('dragDrop')}</p>
                        <input id="fileInput" type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={e => setFiles(Array.from(e.target.files))} />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', alignItems: 'center' }}>
                        <button className="wlink" style={outlineButton} onClick={() => navigate('/admin/dashboard')}>{t('cancel')}</button>
                        <button
                            className="wlink"
                            style={pillButton}
                            onClick={() => {
                                if (!albumTitle) { setUploadError(t('titleRequired')); return; }
                                if (files.length === 0) { setUploadError(t('photoRequired')); return; }
                                if (!creator) { setUploadError(t('creatorRequired')); return; }
                                setUploadError('');
                                setConfirmStep(1);
                            }}
                        >
                            {t('publish')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Double confirmation — deux clics, pas de signature */}
            {confirmStep > 0 && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: theme.card, padding: '28px', width: 'min(360px, calc(100vw - 48px))', boxSizing: 'border-box' }}>
                        {confirmStep === 1 && (
                            <>
                                <h3 style={{ fontFamily: font, fontWeight: 700, marginBottom: '8px', fontSize: '15px', color: theme.text }}>Publier "{albumTitle}" ?</h3>
                                <p style={{ fontFamily: font, fontSize: '13px', fontWeight: 500, color: theme.textMuted, marginBottom: '20px' }}>
                                    Cet album sera visible publiquement sur winston.ie.
                                </p>
                                <div style={{ display: 'flex', gap: '20px', justifyContent: 'flex-end' }}>
                                    <button className="wlink" style={outlineButton} onClick={() => setConfirmStep(0)}>{t('cancel')}</button>
                                    <button className="wlink" style={pillButton} onClick={() => setConfirmStep(2)}>Continuer</button>
                                </div>
                            </>
                        )}
                        {confirmStep === 2 && (
                            <>
                                <h3 style={{ fontFamily: font, fontWeight: 700, marginBottom: '8px', fontSize: '15px', color: theme.text }}>Confirme une dernière fois</h3>
                                <p style={{ fontFamily: font, fontSize: '13px', fontWeight: 500, color: theme.textMuted, marginBottom: '20px' }}>
                                    Es-tu sûr de vouloir publier maintenant ?
                                </p>
                                <div style={{ display: 'flex', gap: '20px', justifyContent: 'flex-end' }}>
                                    <button className="wlink" style={outlineButton} onClick={() => setConfirmStep(0)}>{t('cancel')}</button>
                                    <button className="wlink" style={pillButton} onClick={handlePublish}>Publier</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Upload;